import React, { useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, TextField, Button, Typography, Alert, CircularProgress, Link, InputAdornment, IconButton,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import { authAPI } from '../services/api';
import { brandColors, brandCss } from '../theme/appTheme';
import { authPrimaryButtonSx } from '../theme/formStyles';
import { AuthCoverLayout, authFont } from '../components/auth/AuthCoverLayout';
import { RESET_COVER } from '../components/auth/authCoverContent';
import { authTextFieldSx } from '../components/auth/authFieldStyles';

export const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const token = searchParams.get('token') || '';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (!token) {
      setError('Invalid reset link. Request a new one.');
      return;
    }
    setLoading(true);
    try {
      const res = await authAPI.resetPassword(token, password);
      setSuccess(res.data.message);
      setTimeout(() => navigate('/login'), 2000);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg || 'Reset failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthCoverLayout cover={RESET_COVER}>
      <Typography
        sx={{
          ...authFont.heading,
          fontSize: '1.55rem',
          color: brandColors.textDark,
          mb: 0.5,
        }}
      >
        Reset password
      </Typography>
      <Typography
        sx={{
          ...authFont.body,
          fontSize: 14,
          color: brandColors.textMuted,
          mb: 2.75,
        }}
      >
        Choose a new password for your Tripiigo account
      </Typography>

      {!token && (
        <Alert severity="warning" sx={{ mb: 2, borderRadius: 2.5 }}>
          This link is missing a token.{' '}
          <Link component={RouterLink} to="/forgot-password">Request a new reset link</Link>.
        </Alert>
      )}

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2.5 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2, borderRadius: 2.5 }}>{success}</Alert>}

      <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2.25 }}>
        <TextField
          label="New Password"
          type={showPassword ? 'text' : 'password'}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          fullWidth
          required
          placeholder="Min. 6 characters"
          helperText="Pick something strong and easy for you to remember"
          sx={authTextFieldSx}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockOutlinedIcon sx={{ fontSize: 20, color: brandColors.textMuted }} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setShowPassword((v) => !v)} edge="end">
                  {showPassword ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          label="Confirm Password"
          type={showConfirm ? 'text' : 'password'}
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          fullWidth
          required
          placeholder="Re-enter new password"
          sx={authTextFieldSx}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockOutlinedIcon sx={{ fontSize: 20, color: brandColors.textMuted }} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setShowConfirm((v) => !v)} edge="end">
                  {showConfirm ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <Button
          type="submit"
          variant="contained"
          size="large"
          disabled={loading || !token}
          fullWidth
          endIcon={!loading ? <ArrowForwardRoundedIcon /> : undefined}
          sx={{ ...authPrimaryButtonSx, borderRadius: '4px' }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : 'Reset Password'}
        </Button>
      </Box>

      <Link
        component={RouterLink}
        to="/forgot-password"
        underline="hover"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          mt: 3,
          color: brandCss.primary,
          fontWeight: 700,
          fontFamily: authFont.family,
          fontSize: 13.5,
        }}
      >
        <ArrowBackIcon sx={{ fontSize: 16 }} /> Request new link
      </Link>
    </AuthCoverLayout>
  );
};
